import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import Skeleton from "src/components/Skeleton";
import {
  getNativePdfViewState,
  isNativePdfAvailable,
  mountNativePdfView,
  type NativePdfFrame,
  showNativePdfFind,
  unmountNativePdfView,
  updateNativePdfView,
} from "src/lib/runtime/nativePdf";
import { useNativePdfHiddenForOverlay } from "src/stores/nativePdfOverlayStore";
import { cn } from "src/utilities/shadcnUtils";

const FRAME_SYNC_INTERVAL_MS = 250;
const STATE_POLL_INTERVAL_MS = 300;

/** A frame the native side reads as "hide this view" — see `nativePdfOverlayStore`. */
const HIDDEN_FRAME: NativePdfFrame = { x: 0, y: 0, width: 0, height: 0 };

const makeViewId = () => `native-pdf-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const toFrame = (element: HTMLElement): NativePdfFrame => {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
  };
};

const frameKey = (frame: NativePdfFrame): string =>
  `${Math.round(frame.x)}:${Math.round(frame.y)}:${Math.round(frame.width)}:${Math.round(frame.height)}`;

export interface NativePdfPortalHandle {
  openFind: () => boolean;
}

type NativePdfPortalProps = {
  documentId?: string;
  className?: string;
  loading?: boolean;
  scale?: number;
  rotation?: number;
  pageNumber?: number;
  onVisiblePageChange?: (pageNumber: number) => void;
  onScaleChange?: (scale: number) => void;
  onError?: (error: Error) => void;
};

export const NativePdfPortal = memo(
  forwardRef<NativePdfPortalHandle, NativePdfPortalProps>(function NativePdfPortal(
    {
      documentId,
      className,
      loading,
      scale,
      rotation,
      pageNumber,
      onVisiblePageChange,
      onScaleChange,
      onError,
    },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const viewIdRef = useRef(makeViewId());
    const mountedDocumentIdRef = useRef<string | undefined>(undefined);
    const lastFrameKeyRef = useRef<string | undefined>(undefined);
    const pendingFrameSyncRef = useRef<
      | {
          documentId: string;
          frame: NativePdfFrame;
          scale?: number;
          rotation?: number;
          pageNumber?: number;
        }
      | undefined
    >(undefined);
    const frameSyncPromiseRef = useRef<Promise<void> | null>(null);
    const unmountTimerRef = useRef<number | null>(null);
    const lastReportedPageRef = useRef<number | undefined>(undefined);
    const lastReportedScaleRef = useRef<number | undefined>(undefined);

    const viewId = viewIdRef.current;
    const enabled = isNativePdfAvailable() && !!documentId;
    // A DOM popover or menu is open over the viewer; AppKit would draw this view on top of it.
    const hiddenForOverlay = useNativePdfHiddenForOverlay();

    const currentFrame = useCallback(() => {
      const element = containerRef.current;
      return element ? toFrame(element) : undefined;
    }, []);

    const reportError = useCallback(
      (error: unknown) => {
        onError?.(error instanceof Error ? error : new Error(String(error)));
      },
      [onError]
    );

    useImperativeHandle(
      ref,
      () => ({
        openFind: () => {
          if (!enabled || mountedDocumentIdRef.current !== documentId) {
            return false;
          }
          void showNativePdfFind(viewId).catch(reportError);
          return true;
        },
      }),
      [documentId, enabled, reportError, viewId]
    );

    const syncFrame = useCallback((): Promise<void> => {
      if (!enabled || !documentId) {
        return Promise.resolve();
      }
      const frame = currentFrame();
      if (!frame) {
        return Promise.resolve();
      }

      // ResizeObserver, window resize/scroll, and the fallback timer can all fire while a
      // Tauri invocation is still pending. Keep only the newest requested geometry and drain
      // it serially so an older response can never overwrite a newer native frame.
      pendingFrameSyncRef.current = {
        documentId,
        frame: hiddenForOverlay ? HIDDEN_FRAME : frame,
        scale,
        rotation,
        pageNumber,
      };
      if (frameSyncPromiseRef.current) {
        return frameSyncPromiseRef.current;
      }

      const drainFrameSync = async () => {
        while (pendingFrameSyncRef.current) {
          const request = pendingFrameSyncRef.current;
          pendingFrameSyncRef.current = undefined;

          if (mountedDocumentIdRef.current && mountedDocumentIdRef.current !== request.documentId) {
            await unmountNativePdfView(viewId);
            mountedDocumentIdRef.current = undefined;
            lastFrameKeyRef.current = undefined;
          }

          const nextFrameKey = frameKey(request.frame);
          if (
            lastFrameKeyRef.current === nextFrameKey &&
            mountedDocumentIdRef.current === request.documentId
          ) {
            continue;
          }

          if (mountedDocumentIdRef.current === request.documentId) {
            await updateNativePdfView({ viewId, frame: request.frame });
          } else {
            await mountNativePdfView({
              viewId,
              documentId: request.documentId,
              frame: request.frame,
              scale: request.scale,
              rotation: request.rotation,
              pageNumber: request.pageNumber,
            });
            mountedDocumentIdRef.current = request.documentId;
          }
          // Record a frame only after the native side confirms it was applied. A failed or
          // superseded request must remain retryable by the next observer/timer tick.
          lastFrameKeyRef.current = nextFrameKey;
        }
      };

      const syncPromise = drainFrameSync().finally(() => {
        if (frameSyncPromiseRef.current === syncPromise) {
          frameSyncPromiseRef.current = null;
        }
      });
      frameSyncPromiseRef.current = syncPromise;
      return syncPromise;
    }, [currentFrame, documentId, enabled, hiddenForOverlay, pageNumber, rotation, scale, viewId]);

    const syncFrameSafely = useCallback(() => {
      void syncFrame().catch(reportError);
    }, [reportError, syncFrame]);

    useLayoutEffect(() => {
      if (!enabled) {
        return;
      }

      syncFrameSafely();

      const intervalId = window.setInterval(syncFrameSafely, FRAME_SYNC_INTERVAL_MS);
      const resizeObserver = new ResizeObserver(syncFrameSafely);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      window.addEventListener("resize", syncFrameSafely);
      window.addEventListener("scroll", syncFrameSafely, true);

      return () => {
        window.clearInterval(intervalId);
        resizeObserver.disconnect();
        window.removeEventListener("resize", syncFrameSafely);
        window.removeEventListener("scroll", syncFrameSafely, true);
      };
    }, [enabled, syncFrameSafely]);

    const syncViewOptions = useCallback(async () => {
      if (!enabled || mountedDocumentIdRef.current !== documentId) {
        return;
      }
      await updateNativePdfView({
        viewId,
        scale,
        rotation,
        pageNumber,
      });
    }, [documentId, enabled, pageNumber, rotation, scale, viewId]);

    useEffect(() => {
      void syncViewOptions().catch(reportError);
    }, [reportError, syncViewOptions]);

    const pollState = useCallback(async () => {
      if (!enabled || !mountedDocumentIdRef.current) {
        return;
      }

      const state = await getNativePdfViewState(viewId);
      if (state.currentPage !== lastReportedPageRef.current) {
        lastReportedPageRef.current = state.currentPage;
        onVisiblePageChange?.(state.currentPage);
      }

      const roundedScale = Math.round(state.scale * 100) / 100;
      if (roundedScale !== lastReportedScaleRef.current) {
        lastReportedScaleRef.current = roundedScale;
        onScaleChange?.(roundedScale);
      }
    }, [enabled, onScaleChange, onVisiblePageChange, viewId]);

    const pollStateSafely = useCallback(() => {
      void pollState().catch(reportError);
    }, [pollState, reportError]);

    useEffect(() => {
      if (!enabled) {
        return;
      }

      const intervalId = window.setInterval(pollStateSafely, STATE_POLL_INTERVAL_MS);
      pollStateSafely();

      return () => {
        window.clearInterval(intervalId);
      };
    }, [enabled, pollStateSafely]);

    useEffect(() => {
      if (enabled) {
        return;
      }

      mountedDocumentIdRef.current = undefined;
      lastFrameKeyRef.current = undefined;
      void unmountNativePdfView(viewId).catch(reportError);
    }, [enabled, reportError, viewId]);

    useEffect(() => {
      if (unmountTimerRef.current) {
        window.clearTimeout(unmountTimerRef.current);
        unmountTimerRef.current = null;
      }

      return () => {
        // React development mode replays effects as setup → cleanup → setup. Deferring this
        // teardown by one task lets the replayed setup cancel it; otherwise a late unmount can
        // remove the native view after its queued mount has succeeded, leaving a blank portal.
        pendingFrameSyncRef.current = undefined;
        unmountTimerRef.current = window.setTimeout(() => {
          unmountTimerRef.current = null;
          const pendingSync = frameSyncPromiseRef.current;
          void (async () => {
            try {
              await pendingSync;
            } catch {
              // The originating sync already reports its own error; teardown must still run.
            }
            mountedDocumentIdRef.current = undefined;
            lastFrameKeyRef.current = undefined;
            await unmountNativePdfView(viewId);
          })().catch(reportError);
        }, 0);
      };
    }, [reportError, viewId]);

    const skeleton = useMemo(() => <Skeleton className="h-full w-full rounded-sm" />, []);

    return (
      <div ref={containerRef} className={cn("relative h-full w-full overflow-hidden", className)}>
        {loading || !documentId ? skeleton : null}
      </div>
    );
  })
);
